import { Image } from '@unistocks/components/basic/image';
import { Form } from '@unistocks/components/form/form';
import { Column } from '@unistocks/components/layout/flex';
import { HeightAnimator } from '@unistocks/components/layout/height-animator';
import { Paragraph } from '@unistocks/components/text/text';
import { useAssetSearch } from '@unistocks/features/assets/hooks/use-asset-search';
import { InstrumentDetails } from '@unistocks/features/external-accounts/components/instrument-details';
import { FlagKey, FLAGS } from '@unistocks/features/portfolios/flags';
import { t } from '@unistocks/localization';
import {
  AssetType,
  ExternalAccountDto,
  ExternalAssetSearchDto,
  ExternalPortfolioDto,
  InstrumentDto,
  PortfolioDto,
  SectionAssetDto
} from '@unistocks/proxy/axios-client';
import { assetValueFormatter, formatISINAndSymbol } from '@unistocks/shared/utils/asset-utils';
import { Option, SelectOption } from '@unistocks/types';
import React from 'react';
import { useWatch } from 'react-hook-form';
import { ListItem, Separator } from 'tamagui';
import { z } from 'zod';

export type InstrumentSettingsProps = {
  instrument: InstrumentDto;
  matchingAsset?: SectionAssetDto;
  targetPortfolio: PortfolioDto;
  externalPortfolio: ExternalPortfolioDto;
  externalAccount: ExternalAccountDto;
  previouslyImported: boolean;
  onSave: (values: InstrumentSettingsFormData) => void;
};

export type InstrumentSettingsFormData = {
  sectionId: string;
  selectedInstrument: ExternalAssetSearchDto | null;
  isHidden: boolean;
  isManualAsset: boolean;
};

const renderAssetSearchImage = (item: ExternalAssetSearchDto) => (
  <Image
    rounded
    size={'$1.5'}
    source={item?.imageUri ?? (item.market ? FLAGS[item.market as FlagKey] : undefined)}
  />
);

const extractAssetId = ({ value: { dataSource, identifier } }: Option<ExternalAssetSearchDto>) =>
  `${dataSource}:${identifier}`;

const assetSearchTypes = [
  AssetType.Stock,
  AssetType.Fund,
  AssetType.CryptoCurrency,
  AssetType.Other
];

const schemaFactory = () =>
  z.object({
    selectedInstrument: z.record(z.string(), z.any()).nullish(),
    isHidden: z.boolean(),
    isManualAsset: z.boolean()
  });

const InstrumentSettingsFormContent: React.FC<{
  instrument: InstrumentDto;
  targetPortfolio: PortfolioDto;
  previouslyImported: boolean;
  search: (term: string) => Promise<SelectOption<ExternalAssetSearchDto>[]>;
}> = ({ instrument, targetPortfolio, previouslyImported, search }) => {
  const currentValues = useWatch<InstrumentSettingsFormData>() as InstrumentSettingsFormData;

  const renderAssetSearch = () => {
    if (!assetSearchTypes.includes(instrument.assetType)) return null;

    const selectOptions: SelectOption<ExternalAssetSearchDto>[] = instrument.searchResult.map(
      asset => ({
        key: assetValueFormatter(asset),
        value: asset
      })
    );

    const selected = currentValues.selectedInstrument;

    return !currentValues.isHidden && !currentValues.isManualAsset ? (
      <Column gap={'$3'}>
        {selected ? (
          <Column gap={'$3'}>
            <Paragraph>{t('::Import:ConnectedAsset')}</Paragraph>
            <ListItem
              gap={'$3'}
              title={selected.name}
              subTitle={formatISINAndSymbol(selected)}
              icon={renderAssetSearchImage(selected)}
            />
            <Paragraph>{t('::Import:SearchMatchingAsset')}</Paragraph>
          </Column>
        ) : null}

        <Form.Typeahead
          name={'selectedInstrument'}
          label={t('::Search')}
          placeholder={t('::AssetSearch:Placeholder', {
            0: t(`::Assets`).toLowerCase()
          })}
          helpText={t('::Import:SearchMatchingAsset:HelpText')}
          options={selectOptions}
          keyExtractor={extractAssetId}
          search={search}
          valueFormatter={assetValueFormatter}
          renderItemIcon={({ value }) => (value ? renderAssetSearchImage(value) : null)}
        />
        <Separator mb={'$3'} />
      </Column>
    ) : null;
  };

  const renderSettings = () => {
    return (
      <>
        {!currentValues.isHidden && !previouslyImported ? (
          <Form.Select
            name={'sectionId'}
            label={t('::Import:SelectSection')}
            isRequired
            options={targetPortfolio.sections.map(section => ({
              key: section.name,
              value: section.id
            }))}
            placeholder={t('::SelectWhat', { 0: t('::Category').toLocaleLowerCase() })}
            headerTitle={t('::SelectWhat', { 0: t('::Category').toLocaleLowerCase() })}
          />
        ) : null}

        <Form.Checkbox
          name={'isHidden'}
          label={t('::Import:HideInstrument')}
          helpText={t('::Import:HideInstrument:HelpText')}
        />

        {!currentValues.isHidden && assetSearchTypes.includes(instrument.assetType) ? (
          <Form.Checkbox
            name={'isManualAsset'}
            label={t('::Import:ManualAsset')}
            helpText={t('::Import:ManualAsset:HelpText')}
          />
        ) : null}
      </>
    );
  };

  return (
    <HeightAnimator>
      {renderAssetSearch()}
      {renderSettings()}
    </HeightAnimator>
  );
};

export const InstrumentSettings: React.FC<InstrumentSettingsProps> = ({
  instrument,
  targetPortfolio,
  externalPortfolio,
  externalAccount,
  previouslyImported,
  onSave
}) => {
  const { search } = useAssetSearch(assetSearchTypes);

  const initialValues: InstrumentSettingsFormData = {
    sectionId: instrument.importSettings.sectionId,
    selectedInstrument: instrument.searchResult[0] ?? null,
    isHidden: instrument.importSettings.isHidden,
    isManualAsset: instrument.importSettings.isManualAsset
  };

  return (
    <Column gap={'$3'}>
      <InstrumentDetails
        instrument={instrument}
        externalAccount={externalAccount}
        portfolio={externalPortfolio}
      />

      <Separator />

      <Form initialValues={initialValues} schema={schemaFactory()} onSubmit={onSave}>
        <InstrumentSettingsFormContent
          instrument={instrument}
          targetPortfolio={targetPortfolio}
          previouslyImported={previouslyImported}
          search={search}
        />
      </Form>
    </Column>
  );
};

InstrumentSettings.displayName = 'InstrumentSettings';
